import { supabase } from '@/core/infrastructure/supabase/supabaseClient';

// Helper para convertir la clave pública VAPID (Base64URL) a un Uint8Array para pushManager
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replaceAll('-', '+')
    .replaceAll('_', '/');

  const rawData = globalThis.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.codePointAt(i) ?? 0;
  }
  return outputArray;
}

/**
 * PushService
 * 
 * Gestiona el registro del Service Worker y el ciclo de vida de la suscripción
 * de notificaciones Web Push de acuerdo con la Sección 6 del SDD de Chat Seguro.
 */
export class PushService {
  private static readonly VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

  /**
   * Intenta registrar el Service Worker y suscribir al usuario actual a las notificaciones Web Push.
   * Maneja fallos silenciosamente para cumplir con la directriz "Anti-Fallo en Cascada".
   */
  static async registerAndSubscribe(): Promise<void> {
    // 1. Validar soporte en el navegador (PC, Android o iOS 16.4+ instalado como PWA)
    if (!('serviceWorker' in navigator) || !('PushManager' in globalThis)) {
      console.warn('Las notificaciones Push no están soportadas en este navegador o entorno.');
      return;
    }

    try {
      // 2. Registrar el Service Worker
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });
      
      // Esperar a que el SW esté activo
      await navigator.serviceWorker.ready;

      // 3. Solicitar permisos de notificación
      let permission = Notification.permission;
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }

      if (permission !== 'granted') {
        console.info('El usuario denegó los permisos para recibir notificaciones.');
        return;
      }

      // 4. Obtener usuario autenticado actual
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.user) {
        console.info('No hay sesión de usuario activa. Omitiendo suscripción Web Push.');
        return;
      }

      const userId = session.user.id;

      // 5. Suscribir al canal de Push Manager
      if (!this.VAPID_PUBLIC_KEY) {
        console.error('Error: VITE_VAPID_PUBLIC_KEY no está configurada.');
        return;
      }

      const applicationServerKey = urlBase64ToUint8Array(this.VAPID_PUBLIC_KEY);
      
      let subscription = await registration.pushManager.getSubscription();
      
      subscription ??= await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as any
      });

      // 6. Subir la suscripción a Supabase (usuario_push_subscriptions)
      // Usamos un bloque try-catch aislado para que fallos de conexión no tumben la UI
      try {
        const { error: dbError } = await supabase
          .from('usuario_push_subscriptions')
          .upsert({
            usuario_id: userId,
            subscription: subscription.toJSON()
          }, {
            onConflict: 'usuario_id,subscription'
          });

        if (dbError) {
          console.error('Error guardando la suscripción Web Push en la base de datos:', dbError);
        } else {
          console.log('Suscripción Web Push registrada con éxito para el usuario:', userId);
        }
      } catch (dbErr) {
        console.error('Error de red al guardar la suscripción Web Push:', dbErr);
      }

    } catch (error) {
      console.error('Error en el flujo de inicialización de PushService:', error);
    }
  }

  /**
   * Elimina la suscripción activa del dispositivo tanto a nivel de navegador como de base de datos
   */
  static async unsubscribe(): Promise<void> {
    if (!('serviceWorker' in navigator) || !('PushManager' in globalThis)) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        const subscriptionJSON = subscription.toJSON();
        
        // 1. Dar de baja en el navegador
        await subscription.unsubscribe();

        // 2. Remover de Supabase
        const { error: dbError } = await supabase
          .from('usuario_push_subscriptions')
          .delete()
          .match({ subscription: subscriptionJSON });

        if (dbError) {
          console.error('Error eliminando la suscripción Web Push de la base de datos:', dbError);
        }
      }
    } catch (error) {
      console.error('Error al dar de baja las notificaciones Push:', error);
    }
  }
}
